import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { isbn13 } from '../fieldTypes';
import { fieldOptionLabel, optionValue, presetSources, selectLabel, useKinds } from '../kinds';
import { attributionOf, sourceLabel, useSources } from '../sources';
import { Alert, Button, Pill, SearchField, SegmentedControl, Select, Spinner } from '../ui';
import { sourceError } from './TmdbSearch';

const SEARCH_DEBOUNCE_MS = 400;
export const MIN_QUERY = 2;
const CACHE_SIZE = 20;
// What to type, by model: a book's title, author or ISBN; a film's or a series' title.
const PLACEHOLDERS = { movie: 'sourceSearch.placeholderMovie', tv: 'sourceSearch.placeholderTv', videogame: 'sourceSearch.placeholderGame' };

/** A video game type's platform field (phase 4 §4b.4): the search can be narrowed to it. */
export const platformFieldOf = (itemType) => (itemType && itemType.preset === 'videogame'
  ? ((itemType.fields || []).find(field => field.key === 'platform' && field.type === 'select' && !field.unused) || null) : null);

// The last answers, by type, source, language and query: typing back to one of the last 20 asks nobody again.
const searchCache = new Map();
const remember = (key, answer) => {
  searchCache.delete(key);
  searchCache.set(key, answer);
  while (searchCache.size > CACHE_SIZE) searchCache.delete(searchCache.keys().next().value);
};
/** What is owned has changed (an item was added): searches are asked again. */
export const forgetSourceSearches = () => searchCache.clear();

/** A candidate's key: the same edition from two sources is two rows. */
export const candidateKey = (candidate) => (candidate ? `${candidate.source}:${candidate.source_id}` : '');

/**
 * Searching a type's sources as you type (phase 4 §3.4) — the generic
 * search of the presets with several sources (books): one box for a title
 * and an author; an ISBN typed or pasted (hyphens, 10 or 13 digits) is looked
 * up instead, through the type's sources in order — the server asks the BnF
 * first for French ISBNs. Text goes to the source picked above the box
 * (Open Library by default; the BnF for French titles it misses). Results
 * show their cover, authors, year, publisher, the source they come from and
 * "already in a collection". As TmdbSearch: a pause of 400 ms sends a
 * search, a stale answer is dropped, the last 20 are not asked again.
 * `initialQuery`: the box starts with it (a relink searches the item's title).
 * Video games (§4b.4): a platform picker narrows IGDB's search to the
 * copy's console (`platform` / `onPlatform` when the caller keeps it, else
 * its own); results found only without it say so, and owned copies say
 * their platform. A source that is not connected (no key) says so instead.
 */
function SourceSearch({ itemType, lang, selected, onSelect, onManual, autoFocus = true, listStyle, initialQuery = '',
  platform: platformProp, onPlatform }) {
  const { t } = useTranslation();
  const kinds = useKinds();
  const sources = useSources();
  // The type's sources that search by text, in its own order (its integration's; the model's for a type of before).
  const typeSources = Array.isArray(itemType.sources) ? itemType.sources : presetSources(itemType.preset, kinds);
  const searchable = useMemo(() => typeSources.filter((id) => {
    const known = sources.find(source => source.id === id);
    return !known || known.capabilities?.search !== false;
  }), [typeSources.join(','), sources]); // eslint-disable-line react-hooks/exhaustive-deps
  const [source, setSource] = useState(null);
  const current = source && searchable.includes(source) ? source : searchable[0];
  const [query, setQuery] = useState(initialQuery);
  const platformField = platformFieldOf(itemType);
  const [ownPlatform, setOwnPlatform] = useState('');
  const platform = platformField ? ((platformProp !== undefined ? platformProp : ownPlatform) || '') : '';
  const choosePlatform = (code) => {
    if (onPlatform) onPlatform(code);
    else setOwnPlatform(code);
    onSelect(null);
  };
  const notConnected = searchable.length > 0 && searchable.every(id => sources.find(source => source.id === id)?.configured === false);
  const [answer, setAnswer] = useState(null);   // null: nothing searched yet; else {source, results, lookup?, sources_tried?}
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const counter = useRef(0);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const timer = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [autoFocus]);

  const isbn = isbn13(query) || null;

  useEffect(() => {
    const q = query.trim();
    if (!current || notConnected || (q.length < MIN_QUERY && !isbn)) {
      counter.current += 1;   // an answer still on its way is now stale
      setAnswer(null);
      setSearching(false);
      return undefined;
    }
    // An ISBN is looked up whichever source is picked: one key for all of them.
    const key = [itemType.id, isbn ? 'isbn' : current, lang, isbn || q.toLowerCase(), platform].join('|');
    if (searchCache.has(key)) {
      counter.current += 1;
      setAnswer(searchCache.get(key));
      setSearching(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      const request = ++counter.current;
      setSearching(true);
      api.get(`/sources/${current}/search`, { params: { item_type_id: itemType.id, q: isbn || q, lang, ...(platform ? { platform } : {}) } })
        .then((response) => {
          const data = { ...response.data, results: response.data.results || [] };
          remember(key, data);
          if (request !== counter.current) return;
          setAnswer(data);
          setError(null);
        })
        .catch((err) => {
          if (request !== counter.current) return;
          setAnswer(null);   // the previous search's results are not this one's
          setError(sourceError(t, err));
        })
        .finally(() => { if (request === counter.current) setSearching(false); });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, current, itemType.id, lang, isbn, platform, notConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const results = answer ? answer.results : null;
  const name = (id) => sourceLabel(id, sources, lang);
  const shown = query.trim().length >= MIN_QUERY || !!isbn;
  const status = () => {
    if (searching) return <><Spinner size={14} /> {isbn ? t('sourceSearch.lookingUp') : t('addFromSource.searching')}</>;
    if (!results || !shown) return isbn ? t('sourceSearch.isbnDetected') : '';
    if (answer.lookup) {
      const failed = (answer.sources_failed || []).map(f => name(f.source));
      if (results.length === 0 && failed.length > 0) {
        return t('sourceSearch.someFailed', { sources: failed.join(', ') });
      }
      return results.length > 0
        ? t('sourceSearch.foundIn', { source: name(answer.source), count: results.length })
        : t('sourceSearch.notFoundAnywhere', { sources: (answer.sources_tried || []).map(name).join(', ') });
    }
    return t('addFromSource.resultCount', { count: results.length });
  };
  // The attributions of the sources whose results are listed (the BnF's Licence ouverte asks for it).
  const credited = [...new Set((results || []).map(result => result.source))]
    .map(id => attributionOf(id, sources, lang)).filter(Boolean);

  // Owned copies of a game say their platforms ("Possédé — Switch"); a book's or a film's say nothing more.
  const ownedLabel = (owned) => {
    const platforms = platformField ? [...new Set(owned.map(item => item.platform).filter(Boolean))] : [];
    return platforms.length > 0
      ? t('sourceSearch.ownedOn', { platforms: platforms.map(code => selectLabel(platformField, code, lang)).join(', ') })
      : t('addFromSource.owned');
  };

  if (notConnected) {
    return (
      <div className="tmdb-search source-search">
        <Alert tone="info" title={t('sourceSearch.notConnected', { source: name(searchable[0]) })}>
          {t('sourceSearch.notConnectedHint')}
        </Alert>
        {onManual && <Button variant="secondary" size="sm" onClick={onManual}>{t('addFromSource.enterByHand')}</Button>}
      </div>
    );
  }

  return (
    <div className="tmdb-search source-search">
      {searchable.length > 1 && (
        <SegmentedControl label={t('sourceSearch.searchIn')} size="sm" value={current} className="source-search__sources"
          onChange={(id) => { setSource(id); onSelect(null); setError(null); }}
          options={searchable.map(id => ({ id, label: name(id) }))} />
      )}
      <div className="tmdb-search__fields">
        {platformField && (
          <Select label={t('sourceSearch.platform')} value={platform} className="source-search__platform"
            onChange={(event) => choosePlatform(event.target.value)}>
            <option value="">{t('sourceSearch.anyPlatform')}</option>
            {(platformField.options || []).map(option => (
              <option key={optionValue(option)} value={optionValue(option)}>{fieldOptionLabel(platformField, option, lang)}</option>
            ))}
          </Select>
        )}
        <SearchField ref={searchRef} value={query} className="tmdb-search__query"
          onChange={(text) => { setQuery(text); onSelect(null); setError(null); }}
          placeholder={t(PLACEHOLDERS[itemType.preset] || 'sourceSearch.placeholder')}
          label={t('sourceSearch.label', { source: name(current) })} />
      </div>

      <p aria-live="polite" className="tmdb-search__status">{status()}</p>
      {error && <Alert tone="danger">{error}</Alert>}

      <ul aria-label={t('addFromSource.results')} className="tmdb-results" style={listStyle}>
        {(results || []).map((result) => {
          const isSelected = candidateKey(selected) === candidateKey(result);
          const meta = [result.year, result.publisher].filter(Boolean).join(' · ');
          return (
            <li key={candidateKey(result)}>
              <button type="button" className={`source-result${isSelected ? ' is-selected' : ''}`}
                aria-pressed={isSelected} onClick={() => onSelect(result)}>
                {result.thumb
                  ? <img className="source-poster" src={result.thumb} alt="" loading="lazy" />
                  : <span className="source-poster source-poster--empty" aria-hidden="true" />}
                <span className="source-result__text">
                  <span className="source-result__title">{result.title}</span>
                  {result.subtitle && <span className="source-result__original">{result.subtitle}</span>}
                  {result.creators && result.creators.length > 0 && <span className="source-result__overview">{result.creators.join(', ')}</span>}
                  {meta && <span className="source-result__meta">{meta}</span>}
                  {result.identifiers?.isbn && <span className="source-result__meta">ISBN {result.identifiers.isbn}</span>}
                  <span className="source-result__badges">
                    <Pill className="source-result__source">{name(result.source)}</Pill>
                    {result.owned && result.owned.length > 0 && <Pill tone="amber" className="source-result__owned">{ownedLabel(result.owned)}</Pill>}
                    {result.other_platform && <Pill className="source-result__other">{t('sourceSearch.otherPlatform', { source: name(result.source) })}</Pill>}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
        {results && results.length === 0 && !searching && shown && (
          <li className="tmdb-results__none">
            <span>{answer.lookup
              ? ((answer.sources_failed || []).length > 0 ? t('sourceSearch.notYet') : t('sourceSearch.noEdition'))
              : t('sourceSearch.noResult', { source: name(current) })}</span>
            {onManual && <Button variant="secondary" size="sm" onClick={onManual}>{t('addFromSource.enterByHand')}</Button>}
          </li>
        )}
      </ul>
      {credited.length > 0 && (
        <p className="source-search__credits">
          {credited.map((credit, index) => (
            <React.Fragment key={credit.url}>
              {index > 0 && ' · '}
              <a href={credit.url} target="_blank" rel="noopener noreferrer">{credit.text}</a>
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
}

export default SourceSearch;
